import { Link } from "gatsby";
import { microCopy } from "@bmi-digital/dxb-microcopies";
import { GetMicroCopy } from "../../components/MicroCopy";
import type { SuggestionGroup } from "@bmi-digital/components";

interface ElasticSearchProductHit {
  _source: {
    name: string;
    path: string;
    code: string;
  };
}

interface ElasticSearchPageHit {
  _source: {
    title: string;
    path: string;
  };
}

type PimDocumentSource = {
  title: string;
  url: string;
  asset?: never;
};

type PayloadDocumentSource = {
  title: string;
  url?: never;
  asset: {
    file: {
      url: string;
    };
  };
};

export interface ElasticSearchDocumentHit {
  _source: PimDocumentSource | PayloadDocumentSource;
}

export interface ElasticSearchResponse<T> {
  hits: {
    hits: T[];
    total: {
      value: number;
    };
  };
  aggregations?: {
    unique_base_products_count?: {
      value: number;
    };
    total?: {
      value: number;
    };
  };
}

export interface FormatSuggestionsParams {
  products: ElasticSearchResponse<ElasticSearchProductHit> | null;
  pages: ElasticSearchResponse<ElasticSearchPageHit> | null;
  documents: ElasticSearchResponse<ElasticSearchDocumentHit> | null;
  getMicroCopy: GetMicroCopy;
  countryCode: string;
  term: string;
  variantCodeToPathMap?: Record<string, string>;
}

export const formatSuggestions = ({
  products,
  pages,
  documents,
  getMicroCopy,
  countryCode,
  term,
  variantCodeToPathMap
}: FormatSuggestionsParams): SuggestionGroup[] => {
  const groups: SuggestionGroup[] = [];

  const isGroupSite = process.env.GATSBY_DONT_USE_COUNTRY_CODE === "true";
  const basePath = isGroupSite ? "" : `/${countryCode}`;

  const searchTabHeadingPagesLabel = getMicroCopy(
    microCopy.SEARCH_TAB_HEADINGS_PAGES
  );
  const searchTabHeadingProductLabel = getMicroCopy(
    microCopy.SEARCH_TAB_HEADINGS_PRODUCTS
  );
  const searchTabHeadingDocumentsLabel = getMicroCopy(
    microCopy.SEARCH_TAB_HEADINGS_DOCUMENTS
  );
  const matchesLabel = getMicroCopy(microCopy.SEARCH_MATCHES);
  const viewMoreLabel = getMicroCopy(microCopy.GLOBAL_VIEW_MORE);

  const totalProductHits =
    products?.aggregations?.unique_base_products_count?.value ??
    products?.hits?.total?.value ??
    0;

  const totalPagesHits = pages?.hits?.total?.value || 0;
  const totalDocumentHits =
    documents?.aggregations?.total?.value ?? documents?.hits?.total?.value ?? 0;

  const productUrl = `${basePath}/search/?q=${term}&tab=products`;
  const pageUrl = `${basePath}/search/?q=${term}&tab=pages`;
  const documentUrl = `${basePath}/search/?q=${term}&tab=documents`;

  if (totalProductHits > 0 && products) {
    groups.push({
      header: {
        label: searchTabHeadingProductLabel,
        subLabel: `${totalProductHits} ${matchesLabel}`
      },
      items: products.hits.hits.map(
        ({ _source: { name, path, code } }: ElasticSearchProductHit) => {
          // eslint-disable-next-line security/detect-object-injection
          const finalPath = variantCodeToPathMap?.[code] || path;
          const formattedProductPath = finalPath.startsWith("/")
            ? finalPath
            : `/${finalPath}`;

          const finalUrl = isGroupSite
            ? formattedProductPath
            : `/${countryCode}${formattedProductPath}`;

          return {
            label: name,
            value: finalUrl,
            to: finalUrl,
            component: Link,
            gtm: {
              id: "cta-click1",
              label: name,
              action: finalUrl
            }
          };
        }
      ),
      footer: {
        label: viewMoreLabel,
        gtm: {
          id: "cta-click1",
          label: viewMoreLabel,
          action: productUrl
        },
        component: Link,
        to: productUrl
      }
    });
  }

  if (totalPagesHits > 0 && pages) {
    groups.push({
      header: {
        label: searchTabHeadingPagesLabel,
        subLabel: `${totalPagesHits} ${matchesLabel}`
      },
      items: pages.hits.hits.map(
        ({ _source: { title, path } }: ElasticSearchPageHit) => {
          const formattedPagePath = path.startsWith("/") ? path : `/${path}`;

          const finalUrl = isGroupSite
            ? formattedPagePath
            : `/${countryCode}${formattedPagePath}`;

          return {
            label: title,
            value: finalUrl,
            to: finalUrl,
            component: Link,
            gtm: {
              id: "cta-click1",
              label: title,
              action: finalUrl
            }
          };
        }
      ),
      footer: {
        label: viewMoreLabel,
        gtm: {
          id: "cta-click1",
          label: viewMoreLabel,
          action: pageUrl
        },
        component: Link,
        to: pageUrl
      }
    });
  }

  if (totalDocumentHits > 0 && documents) {
    groups.push({
      header: {
        label: searchTabHeadingDocumentsLabel,
        subLabel: `${totalDocumentHits} ${matchesLabel}`
      },
      items: documents.hits.hits.map(
        ({ _source: { title, url, asset } }: ElasticSearchDocumentHit) => ({
          label: title,
          value: url || asset?.file.url,
          href: url || asset?.file.url,
          external: true,
          gtm: {
            id: "cta-click1",
            label: title,
            action: url || asset?.file.url
          }
        })
      ),
      footer: {
        label: viewMoreLabel,
        gtm: {
          id: "cta-click1",
          label: viewMoreLabel,
          action: documentUrl
        },
        component: Link,
        to: documentUrl
      }
    });
  }

  return groups;
};
